import {
  useDeclineMakeGoodMutation,
  useGetMakeGoodOffersQuery,
  useRegenerateMakeGoodMutation,
} from 'api/makeGoodLadder'
import type { MakeGoodOffer } from 'api/makeGoodLadder.types'
import { useCallback, useMemo } from 'react'
import { RegenerateErrorKind, regenerateErrorKind } from './regenerateError'

// Stable identity for the skipped/unresolved case. A `= []` destructuring
// default allocates a fresh array every render, which would defeat the memo
// below and hand every consumer a new prop reference each time.
const NO_OFFERS: MakeGoodOffer[] = []

export type MakeGoodCurationOptions = {
  /**
   * Suppress the offers GET on top of the flag gate. For a host that mounts the
   * curation wiring before anything can render it — the config-modal registry,
   * which holds it across every closed row — so a closed surface costs no
   * request. Leave unset wherever the offers drive on-screen values (the
   * Agent-Tuning row subtitles, the Settings pane).
   */
  skip?: boolean
}

export type MakeGoodCuration = {
  offers: MakeGoodOffer[]
  /** RTK Query `isSuccess` — separates "200 with zero offers" from "the BE errored". */
  offersLoaded: boolean
  onDecline: (id: string) => void
  onRegenerate: () => void
  isRegenerating: boolean
  regenerateErrorKind?: RegenerateErrorKind
  /**
   * Whether the last decline write failed. Unlike regenerate, decline has no
   * non-terminal status to special-case (a 409 there meant "the recommended
   * set already matches" — decline has nothing analogous), so a boolean is
   * enough; the ladder owns the copy.
   */
  declineError: boolean
  /**
   * True while a decline mutation is in flight. `useDeclineMakeGoodMutation`
   * has no `fixedCacheKey`, so this hook's result reflects only the LATEST
   * triggered call — declining row B while row A is still pending would let
   * A's later resolution clear B's in-progress state. Gating every row's "×"
   * on this (mirrors isRegenerating) means at most one decline is ever in
   * flight, which sidesteps the race instead of solving it structurally.
   */
  isDeclining: boolean
  /**
   * Reset the regenerate mutation. The result is instance-scoped and outlives a
   * modal close, so a failed attempt would otherwise replay as a stale banner
   * with nothing behind it. Call on open/close/save, or on edit for a surface
   * with no close event (the Settings pane).
   */
  clearFeedback: () => void
  /**
   * Offers the ladder actually renders — declined rows are hidden by
   * MakeGoodLadder; materialized rows render like any other row, so they
   * count — so a row subtitle totalling brand + make-good matches what is on
   * screen.
   */
  visibleCount: number
}

// Make-good ladder curation host wiring (AI-330/AI-331/AI-859/AI-383), extracted
// from the two hosts that had grown identical copies of it: the Settings
// Approved-rewards pane and the Agent-Tuning config modals.
//
// context: the query is deliberately fail-soft, unconditional: a make-good 404
// must degrade to an empty ladder, never blank the working picker (CONTRACT
// §3) — so no consumer folds `isLoading`/`isError` into its own load/error
// gates, and both writes swallow their rejection.
export function useMakeGoodCuration({
  skip = false,
}: MakeGoodCurationOptions = {}): MakeGoodCuration {
  const { data, isSuccess: offersLoaded } = useGetMakeGoodOffersQuery(
    undefined,
    { skip }
  )
  const offers = data ?? NO_OFFERS

  // Regenerate — the section-level control that restores the full recommended
  // ladder (declined tiers return). Likewise immediate, and it invalidates only
  // the make-good tag. The rejection is swallowed here (it would otherwise
  // surface as an unhandled rejection) but is not INVISIBLE: the mutation's
  // error drives an inline message inside the ladder section.
  const [
    regenerateMakeGood,
    {
      isLoading: isRegenerating,
      error: regenerateError,
      reset: resetRegenerate,
    },
  ] = useRegenerateMakeGoodMutation()
  const onRegenerate = useCallback(() => {
    regenerateMakeGood()
      .unwrap()
      .catch(() => {})
  }, [regenerateMakeGood])

  // Decline is an IMMEDIATE write, independent of any staged picker Save: it
  // never touches selectedIds/isDirty, so it can't arm an unsaved-changes
  // prompt. The slice's onQueryStarted only patches the cache on success, so a
  // rejected decline leaves the ladder UNCHANGED — but (AI-1121 follow-up) the
  // rejection itself is not invisible: the mutation's error drives its own
  // inline message, same as regenerate.
  const [declineMakeGood, declineResult] = useDeclineMakeGoodMutation()
  const declineErrorRaw = declineResult?.error
  const resetDecline = declineResult?.reset
  const isDeclining = declineResult?.isLoading ?? false

  // Clears BOTH mutations' stale feedback — a decline and a regenerate write
  // the same section, so "the merchant moved on" (edit, modal close/save)
  // must drop whichever of the two last failed, not just regenerate's.
  const clearFeedback = useCallback(() => {
    resetRegenerate()
    resetDecline?.()
  }, [resetRegenerate, resetDecline])

  const onDecline = useCallback(
    (id: string) => {
      declineMakeGood({ id })
        .unwrap()
        .then(() => clearFeedback())
        .catch(() => {})
    },
    [declineMakeGood, clearFeedback]
  )

  // 409 means the regenerate changed nothing — not terminal; any other failure
  // keeps the cause-neutral retry copy. The ladder owns the wording.
  const kind = regenerateErrorKind(regenerateError)
  // Decline has no non-terminal status to special-case, so any failure is
  // just "failed" — the ladder renders one cause-neutral retry message.
  const declineError = Boolean(declineErrorRaw)

  const visibleCount = useMemo(
    () => offers.filter(o => !o.declined).length,
    [offers]
  )

  return useMemo(
    () => ({
      offers,
      offersLoaded,
      onDecline,
      onRegenerate,
      isRegenerating,
      regenerateErrorKind: kind,
      declineError,
      isDeclining,
      clearFeedback,
      visibleCount,
    }),
    [
      offers,
      offersLoaded,
      onDecline,
      onRegenerate,
      isRegenerating,
      kind,
      declineError,
      isDeclining,
      clearFeedback,
      visibleCount,
    ]
  )
}
